/* quick-add.js - Quick Add drawer controller (block_quick_add).
   Re-homed from Madrid's <quick-add-modal> popup onto the shared drawer-shell
   (snippets/quick-add-drawer.liquid renders ONE <modal-drawer id="QuickAddDrawer">
   from theme.liquid). This file:

   - owns the card/PLP triggers ([data-quick-add-open]) via a single delegated
     document listener (one owner per trigger, swap-safe across Section
     Rendering re-renders). The product-card wishlist heart (.card__wishlist) is
     NOT ours - swym-wishlist.js owns it.
   - fetches the product's `quick-add-content` section fragment through the
     Section Rendering API and distributes it into the drawer's body/footer slots
   - drives the image carousel (scroll-snap slides, dots, chevron arrows)
   - handles colour swatch + size chip selection: updates the hidden name="id"
     input, price, stock message and Add-to-bag disabled state
   - the AJAX add itself is product-form.js (the fragment renders a
     <product-form>); on variant:add from this drawer, the drawer closes and
     the cart drawer opens per the Madrid add contract.

   No user-facing strings live here - all copy arrives via data-* attributes
   rendered from Liquid `| t` calls. */

(function () {
	"use strict";

	// theme.liquid + compare / product-carousel sections may each emit this
	// script tag; guard so the delegated listeners bind exactly once
	// (one owner per trigger).
	if (window.__quickAddDrawerInit) return;
	window.__quickAddDrawerInit = true;

	var DRAWER_SELECTOR = "#QuickAddDrawer";
	var quickAddState = null;

	function getDrawer() {
		return document.querySelector(DRAWER_SELECTOR);
	}

	function setTriggerLoading(trigger, isLoading) {
		if (!trigger) return;
		trigger.classList.toggle("loading", isLoading);
		if (isLoading) {
			trigger.setAttribute("aria-disabled", "true");
		} else {
			trigger.removeAttribute("aria-disabled");
		}
		var spinner = trigger.querySelector(".loading-overlay__spinner");
		if (spinner) spinner.classList.toggle("hidden", !isLoading);
	}

	function fetchFragment(productUrl) {
		var separator = productUrl.indexOf("?") > -1 ? "&" : "?";
		var url = productUrl + separator + "section_id=quick-add-content";
		return fetch(url).then(function (response) {
			if (!response.ok) throw new Error("quick-add fragment " + response.status);
			return response.text();
		});
	}

	function openQuickAdd(trigger) {
		var productUrl = trigger.getAttribute("data-product-url");
		if (!productUrl) return;
		var drawer = getDrawer();
		if (!drawer) return;

		setTriggerLoading(trigger, true);

		fetchFragment(productUrl)
			.then(function (html) {
				var doc = new DOMParser().parseFromString(html, "text/html");
				var body = doc.querySelector("[data-quick-add-body]");
				var footer = doc.querySelector("[data-quick-add-footer]");
				var bodySlot = drawer.querySelector("[data-quick-add-body-slot]");
				var footerSlot = drawer.querySelector("[data-quick-add-footer-slot]");
				if (!body || !bodySlot || !footerSlot) return;

				bodySlot.innerHTML = "";
				footerSlot.innerHTML = "";
				bodySlot.appendChild(body);
				if (footer) footerSlot.appendChild(footer);

				initState(drawer);
				initCarousel(drawer);
				// The heart in the fragment is a fresh <button data-wishlist-button>;
				// swym-wishlist.js hydrated saved state once at load, so re-apply it
				// here or an already-saved product opens unfilled (Productive #213).
				if (typeof window.swymReflectAll === "function") window.swymReflectAll();

				// product-form.js only upgrades <product-form> elements already in
				// the DOM at definition time via the custom element lifecycle - the
				// injected element upgrades automatically (customElements).
				drawer.open(trigger);
			})
			.catch(function (error) {
				console.error(error);
			})
			.finally(function () {
				setTriggerLoading(trigger, false);
			});
	}

	/* ---- selection state ---- */

	function initState(drawer) {
		var variantsEl = drawer.querySelector("[data-quick-add-variants]");
		var sizeSection = drawer.querySelector("[data-quick-add-size]");
		var colorSection = drawer.querySelector("[data-quick-add-color]");
		var activeSwatch = colorSection ? colorSection.querySelector("[data-quick-add-swatch].is-active") : null;
		// The size-pills snippet pre-presses option.selected_value (first
		// available variant). The drawer's contract is "no size chosen yet"
		// (Add disabled + select-size message), so clear the visual pre-press
		// to keep UI and state consistent.
		if (sizeSection) {
			sizeSection.querySelectorAll('.size-pills__pill[aria-pressed="true"]').forEach(function (el) {
				el.removeAttribute("aria-pressed");
			});
		}
		/* Productive #346 - pre-order map (variant id -> { preorder, note, lineitem })
		   rendered by snippets/preorder-variants-json.liquid when the product carries
		   a flag|pre-order tag. Absent on every other product. */
		var preorderEl = drawer.querySelector("[data-quick-add-preorder]");
		var preorderMap = null;
		if (preorderEl) {
			try {
				preorderMap = JSON.parse(preorderEl.textContent);
			} catch (e) {
				preorderMap = null;
			}
		}
		quickAddState = {
			variants: variantsEl ? JSON.parse(variantsEl.textContent) : [],
			preorder: preorderMap,
			sizePosition: sizeSection ? Number(sizeSection.getAttribute("data-option-position")) : null,
			colorPosition: colorSection ? Number(colorSection.getAttribute("data-option-position")) : null,
			selectedSize: null,
			selectedColor: activeSwatch ? activeSwatch.dataset.value : null,
		};
	}

	function findVariant() {
		if (!quickAddState) return null;
		var s = quickAddState;
		var match = null;
		for (var i = 0; i < s.variants.length; i++) {
			var v = s.variants[i];
			var sizeOk = s.sizePosition == null || s.selectedSize == null || v.options[s.sizePosition - 1] === s.selectedSize;
			var colorOk = s.colorPosition == null || s.selectedColor == null || v.options[s.colorPosition - 1] === s.selectedColor;
			if (sizeOk && colorOk) {
				if (s.sizePosition != null && s.selectedSize == null) continue;
				match = v;
				if (v.available) return v;
			}
		}
		return match;
	}

	function updateSelectionUI(drawer) {
		var s = quickAddState;
		if (!s) return;
		var submit = drawer.querySelector("[data-quick-add-submit]");
		var idInput = drawer.querySelector("[data-quick-add-variant-id]");
		var stock = drawer.querySelector("[data-quick-add-stock]");
		var priceWrap = drawer.querySelector("[data-quick-add-price]");
		var needsSize = s.sizePosition != null && s.selectedSize == null;
		var variant = findVariant();

		if (variant && idInput) idInput.value = variant.id;

		if (priceWrap && variant) {
			var regular = priceWrap.querySelector(".price-item--regular");
			var sale = priceWrap.querySelector(".price-item--sale");
			if (variant.compare_at_price_formatted) {
				if (regular) regular.textContent = variant.compare_at_price_formatted;
				if (sale) sale.textContent = variant.price_formatted;
				priceWrap.querySelector(".price") && priceWrap.querySelector(".price").classList.add("price--on-sale");
			} else {
				if (regular) regular.textContent = variant.price_formatted;
				priceWrap.querySelector(".price") && priceWrap.querySelector(".price").classList.remove("price--on-sale");
			}
		}

		/* Productive #346 - a pre-order size is `available` to Shopify but ships
		   later. Mirror the PDP (#312): the stock line carries the pre-order title
		   and the size's dispatch note, the button reads "Pre Order", and the cart
		   line gets the pre-order property. */
		var preorderEntry = null;
		if (!needsSize && variant && variant.available && s.preorder) {
			var entry = s.preorder[String(variant.id)];
			if (entry && entry.preorder) preorderEntry = entry;
		}

		if (stock) {
			if (needsSize) {
				stock.textContent = stock.getAttribute("data-message-select");
			} else if (preorderEntry) {
				var title = stock.getAttribute("data-message-preorder") || "";
				stock.textContent = preorderEntry.note ? title + ". " + preorderEntry.note : title;
			} else if (variant && variant.available) {
				stock.textContent = stock.getAttribute("data-message-in-stock");
			} else {
				stock.textContent = stock.getAttribute("data-message-sold-out");
			}
			stock.classList.toggle("quick-add-drawer__stock-message--preorder", !!preorderEntry);
		}

		if (submit) {
			var disable = needsSize || !variant || !variant.available;
			submit.toggleAttribute("disabled", disable);
			if (disable) {
				submit.setAttribute("aria-disabled", "true");
			} else {
				submit.removeAttribute("aria-disabled");
			}
			var label = submit.querySelector(".quick-add-drawer__submit-label");
			if (label && label.hasAttribute("data-label-default")) {
				label.textContent = preorderEntry
					? label.getAttribute("data-label-preorder") || label.getAttribute("data-label-default")
					: label.getAttribute("data-label-default");
			}
		}

		var property = drawer.querySelector("[data-quick-add-preorder-property]");
		if (property) {
			var value = preorderEntry && preorderEntry.lineitem ? preorderEntry.lineitem : "";
			property.value = value;
			property.disabled = !value;
		}

		/* QA #230: a resolved, sold-out selection swaps the (disabled) Add-to-bag
		   form for the Notify CTA and points it at the chosen variant. "No size
		   chosen yet" keeps the disabled Add-to-bag + select-size prompt. */
		var notify = drawer.querySelector("[data-quick-add-notify]");
		var form = drawer.querySelector(".quick-add-drawer__form");
		if (notify) {
			var soldOut = !needsSize && !!variant && !variant.available;
			notify.hidden = !soldOut;
			if (form) form.hidden = soldOut;
			if (soldOut) {
				notify.setAttribute("data-variant-id", variant.id);
				var base = notify.getAttribute("data-product-url");
				if (base) notify.setAttribute("href", base + "?variant=" + variant.id);
			}
		}

		if (variant && variant.featured_media_id) {
			var slide = drawer.querySelector('[data-quick-add-slide][data-media-id="' + variant.featured_media_id + '"]');
			if (slide) slide.scrollIntoView({ block: "nearest", inline: "start", behavior: "auto" });
		}
	}

	/* ---- back in stock hand-off (QA #230) ----
	   The Back in Stock widget binds its own click on [data-bis-app-block] and
	   stops propagation, so this only runs when the widget did not handle the
	   click. If its popover API is present, open it for the variant and swallow
	   the navigation; otherwise let the anchor take the shopper to the PDP at that
	   variant, where the notify flow always exists. */
	document.addEventListener("click", function (event) {
		var trigger = event.target.closest && event.target.closest("[data-quick-add-notify]");
		if (!trigger) return;
		var variantId = parseInt(trigger.getAttribute("data-variant-id"), 10);
		/* Amp's documented API is BIS.popup.show({ variantId }) - help.useamp.com
		   /article/757-javascript-api. BISPopover.show is the older global the PDP
		   integration probes for; try the documented one first. */
		var api = null;
		if (window.BIS && window.BIS.popup && typeof window.BIS.popup.show === "function") {
			api = function () { window.BIS.popup.show({ variantId: variantId }); };
		} else if (window.BISPopover && typeof window.BISPopover.show === "function") {
			api = function () { window.BISPopover.show({ variantId: variantId, variant_id: variantId }); };
		}
		if (!api) return;
		try {
			api();
			event.preventDefault();
		} catch (e) {
			/* Unknown API shape - fall through to the PDP link. */
		}
	});

	/* ---- carousel ---- */

	function initCarousel(drawer) {
		var slides = drawer.querySelector("[data-quick-add-slides]");
		var dots = drawer.querySelectorAll("[data-quick-add-dot]");
		if (!slides || !dots.length) return;

		var syncDots = function () {
			var index = Math.round(slides.scrollLeft / slides.clientWidth);
			dots.forEach(function (dot, i) {
				dot.classList.toggle("is-active", i === index);
			});
		};
		slides.addEventListener("scroll", function () {
			window.requestAnimationFrame(syncDots);
		});
	}

	function scrollCarousel(drawer, direction) {
		var slides = drawer.querySelector("[data-quick-add-slides]");
		if (!slides) return;
		slides.scrollBy({ left: direction * slides.clientWidth, behavior: "smooth" });
	}

	/* ---- delegated events (swap-safe: one listener each on document) ---- */

	document.addEventListener("click", function (event) {
		/* `.card__wishlist` is NOT an opener - the wishlist heart belongs to
		   swym-wishlist.js (one owner per trigger). */
		var opener = event.target.closest("[data-quick-add-open]");
		if (opener) {
			event.preventDefault();
			openQuickAdd(opener);
			return;
		}

		var drawer = event.target.closest(DRAWER_SELECTOR);
		if (!drawer) return;

		var prev = event.target.closest("[data-quick-add-prev]");
		if (prev) return scrollCarousel(drawer, -1);
		var next = event.target.closest("[data-quick-add-next]");
		if (next) return scrollCarousel(drawer, 1);

		var dot = event.target.closest("[data-quick-add-dot]");
		if (dot) {
			var dotsAll = Array.prototype.slice.call(drawer.querySelectorAll("[data-quick-add-dot]"));
			var slides = drawer.querySelector("[data-quick-add-slides]");
			if (slides) {
				slides.scrollTo({ left: dotsAll.indexOf(dot) * slides.clientWidth, behavior: "smooth" });
			}
			return;
		}

		// Linked-colour swatch (Billini colourway = separate product): re-fetch
		// the fragment for the linked product and swap the drawer content
		// in place, keeping the drawer open.
		var linkedSwatch = event.target.closest("[data-quick-add-linked-swatch]");
		if (linkedSwatch) {
			var linkedUrl = linkedSwatch.getAttribute("data-product-url");
			if (linkedUrl) {
				fetchFragment(linkedUrl)
					.then(function (html) {
						var doc = new DOMParser().parseFromString(html, "text/html");
						var body = doc.querySelector("[data-quick-add-body]");
						var footer = doc.querySelector("[data-quick-add-footer]");
						var bodySlot = drawer.querySelector("[data-quick-add-body-slot]");
						var footerSlot = drawer.querySelector("[data-quick-add-footer-slot]");
						if (!body || !bodySlot || !footerSlot) return;
						bodySlot.innerHTML = "";
						footerSlot.innerHTML = "";
						bodySlot.appendChild(body);
						if (footer) footerSlot.appendChild(footer);
						initState(drawer);
						initCarousel(drawer);
						// The heart in the fragment is a fresh <button data-wishlist-button>;
						// swym-wishlist.js hydrated saved state once at load, so re-apply it
						// here or an already-saved product opens unfilled (Productive #213).
						if (typeof window.swymReflectAll === "function") window.swymReflectAll();
					})
					.catch(function (error) {
						console.error(error);
					});
			}
			return;
		}

		var swatch = event.target.closest("[data-quick-add-swatch]");
		if (swatch && quickAddState) {
			quickAddState.selectedColor = swatch.dataset.value;
			drawer.querySelectorAll("[data-quick-add-swatch]").forEach(function (el) {
				var active = el === swatch;
				el.classList.toggle("is-active", active);
				el.setAttribute("aria-pressed", active ? "true" : "false");
			});
			var colorValue = drawer.querySelector("[data-quick-add-color-value]");
			if (colorValue) colorValue.textContent = swatch.dataset.value;
			updateSelectionUI(drawer);
			return;
		}

		// Size chips: the fragment renders product-card-size-pills WITHOUT the
		// <quick-add-size> immediate-add wrapper, so this is the pills' only
		// owner inside the drawer.
		var pill = event.target.closest(".size-pills__pill:not([disabled])");
		if (pill && quickAddState) {
			quickAddState.selectedSize = pill.dataset.value;
			drawer.querySelectorAll(".size-pills__pill").forEach(function (el) {
				if (el === pill) {
					el.setAttribute("aria-pressed", "true");
				} else {
					el.removeAttribute("aria-pressed");
				}
			});
			updateSelectionUI(drawer);
		}
	});

	// Successful AJAX add from the drawer's product-form: close the quick-add
	// drawer; product-form.js then opens the cart drawer via renderContents.
	document.addEventListener("variant:add", function (event) {
		if (!event.detail || event.detail.sectionId !== "quick-add-drawer") return;
		var drawer = getDrawer();
		if (drawer && drawer.classList.contains("active")) drawer.close();
	});
})();
